/**
 * @file reportCatalog.ts
 * @brief Super Admin report catalog metadata (Phase A P0 + backlog stubs).
 *
 * @details
 * IDs match docs/product/reporting.md. Only `available: true` entries are
 * navigable; P1/P2 stay listed as Coming soon so the hub mirrors the full
 * product catalog without inventing unfinished reports.
 *
 * @references
 * - docs/product/reporting.md
 *
 * @author DES
 * @date 27-07-2026
 */

import {
  Briefcase,
  Building2,
  ChartLine,
  Coins,
  DollarSign,
  Languages,
  type LucideIcon,
  ShieldAlert,
  TriangleAlert,
} from "lucide-react";

export type AdminReportId =
  | "sa-revenue-product"
  | "sa-company-health"
  | "sa-cost-margin"
  | "sa-language-demand"
  | "sa-billing-anomalies"
  | "sa-growth"
  | "sa-enterprise-pipeline"
  | "sa-quality-ops";

export type AdminReportMeta = {
  id: AdminReportId;
  title: string;
  description: string;
  shape: "commercial" | "adoption" | "operational" | "insight";
  priority: "P0" | "P1" | "P2";
  available: boolean;
  icon: LucideIcon;
};

export const ADMIN_REPORT_CATALOG: AdminReportMeta[] = [
  {
    id: "sa-revenue-product",
    title: "Revenue by product",
    description:
      "Interpretation, Meeting Intel, Personal Notes, and Plan seats — invoices, revenue, cost, and profit.",
    shape: "commercial",
    priority: "P0",
    available: true,
    icon: DollarSign,
  },
  {
    id: "sa-company-health",
    title: "Company health",
    description:
      "Usage league for the selected range, plus idle companies with no recent sessions.",
    shape: "adoption",
    priority: "P0",
    available: true,
    icon: Building2,
  },
  {
    id: "sa-cost-margin",
    title: "Cost vs margin",
    description:
      "Platform revenue, AI cost, and profit for the range — summary plus period breakdown.",
    shape: "commercial",
    priority: "P0",
    available: true,
    icon: Coins,
  },
  {
    id: "sa-language-demand",
    title: "Language demand",
    description: "Platform-wide language-pair demand by session volume.",
    shape: "insight",
    priority: "P1",
    available: true,
    icon: Languages,
  },
  {
    id: "sa-billing-anomalies",
    title: "Billing anomalies",
    description:
      "Failed charges, unpaid plan invoices, incomplete seats, and Enterprise pool overage.",
    shape: "commercial",
    priority: "P1",
    available: true,
    icon: TriangleAlert,
  },
  {
    id: "sa-growth",
    title: "Growth",
    description: "User and company signups over the selected range.",
    shape: "adoption",
    priority: "P2",
    available: true,
    icon: ChartLine,
  },
  {
    id: "sa-enterprise-pipeline",
    title: "Enterprise pipeline",
    description:
      "Inquiries by status/intent plus live Enterprise seats sold vs assigned.",
    shape: "commercial",
    priority: "P2",
    available: true,
    icon: Briefcase,
  },
  {
    id: "sa-quality-ops",
    title: "Quality / ops",
    description:
      "Failed or stuck Meeting Intel plus today's cost-alert status.",
    shape: "operational",
    priority: "P2",
    available: true,
    icon: ShieldAlert,
  },
];

export function getAdminReport(id: string | undefined): AdminReportMeta | undefined {
  if (!id) return undefined;
  return ADMIN_REPORT_CATALOG.find((r) => r.id === id);
}
